import React, { useMemo, useState, useEffect } from 'react';
import {
  Card,
  Table,
  Pagination,
  Row,
  Col,
  Button,
  Alert,
} from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';
import moment from 'moment';
import { formatarMonetario } from '~/Utils/Helpers';

const ITENS_POR_PAGINA = 20;
const MENSAGEM_VAZIO = 'Nenhum cheque encontrado para os critérios informados.';

const formatarData = (valor) => {
  if (!valor) return '';
  const m = moment(valor);
  return m.isValid() ? m.format('DD/MM/YYYY') : '';
};

// DW-0963: cgc_cpf chega como string (ChequeProrrogadoLinhaResponse.CgcCpf
// número→string). Normaliza preservando letras (uppercase) — sem \D-strip, que
// apagaria A-Z de um CNPJ alfanumérico e derrubaria a máscara. CPF (11) segue
// numérico; CNPJ (14) pode ser alfanumérico. Espelha somenteAlfanumerico de
// validationSchemas.js (toUpperCase + [^A-Z0-9]).
const formatarCgcCpf = (valor) => {
  if (valor == null) return '';
  const doc = String(valor).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (doc.length === 11) {
    return doc.replace(
      /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
      '$1.$2.$3-$4'
    );
  }
  if (doc.length === 14) {
    return doc.replace(
      /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    );
  }
  return String(valor);
};

const formatarMoedaCsv = (valor) => {
  if (valor == null || Number.isNaN(Number(valor))) return '';
  return Number(valor)
    .toFixed(2)
    .replace('.', ',');
};

const escapeCsv = (raw) => {
  if (raw == null) return '';
  const str = String(raw);
  if (/[;\n"\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// CSV client-side (Backend recusou endpoint dedicado — ver MEMORY.md §Backend Shared Decisions §8).
// O conjunto completo é exportado, não apenas a página visível (PRD §RF-03).
const construirConteudoCsv = (linhas, subtotaisPorAtivo, totalGeral) => {
  const cabecalho = [
    'Cód. Ativo',
    'GE',
    'Cód. Proposta',
    'Conta Cedente',
    'Apelido Cedente',
    'Conta Portador',
    'Apelido Portador',
    'Valor',
    'Vencimento',
    'Efetivação',
    'CGC/CPF',
    'Nº Cheque',
    'Prorrogações',
    'Prorrogou',
  ];

  const subtotaisMap = {};
  (subtotaisPorAtivo || []).forEach((s) => {
    subtotaisMap[s.codigo_ativo] = s;
  });

  const linhasCsv = [cabecalho.map(escapeCsv).join(';')];
  let ativoCorrente = null;
  linhas.forEach((l) => {
    if (ativoCorrente != null && l.codigo_ativo !== ativoCorrente) {
      const sub = subtotaisMap[ativoCorrente];
      if (sub) {
        linhasCsv.push(
          [
            escapeCsv(
              `Subtotal Ativo ${ativoCorrente} (${sub.quantidade} cheques)`
            ),
            '',
            '',
            '',
            '',
            '',
            '',
            escapeCsv(formatarMoedaCsv(sub.total_valor)),
            '',
            '',
            '',
            '',
            '',
            '',
          ].join(';')
        );
      }
    }
    linhasCsv.push(
      [
        l.codigo_ativo,
        l.codigo_grupo_empresa,
        l.codigo_proposta,
        l.conta_cedente,
        escapeCsv(l.apelido_cedente),
        l.conta_portador,
        escapeCsv(l.apelido_portador),
        escapeCsv(formatarMoedaCsv(l.valor)),
        escapeCsv(formatarData(l.data_vencimento)),
        escapeCsv(formatarData(l.data_efetivacao)),
        escapeCsv(formatarCgcCpf(l.cgc_cpf)),
        escapeCsv(l.numero_cheque_rec),
        l.prorrogacoes,
        l.prorrogou,
      ]
        .map((c) => (c == null ? '' : c))
        .join(';')
    );
    ativoCorrente = l.codigo_ativo;
  });

  if (ativoCorrente != null) {
    const sub = subtotaisMap[ativoCorrente];
    if (sub) {
      linhasCsv.push(
        [
          escapeCsv(
            `Subtotal Ativo ${ativoCorrente} (${sub.quantidade} cheques)`
          ),
          '',
          '',
          '',
          '',
          '',
          '',
          escapeCsv(formatarMoedaCsv(sub.total_valor)),
          '',
          '',
          '',
          '',
          '',
          '',
        ].join(';')
      );
    }
  }

  if (totalGeral) {
    linhasCsv.push(
      [
        escapeCsv(`Total geral (${totalGeral.quantidade} cheques)`),
        '',
        '',
        '',
        '',
        '',
        '',
        escapeCsv(formatarMoedaCsv(totalGeral.total_valor)),
        '',
        '',
        '',
        '',
        '',
        '',
      ].join(';')
    );
  }

  return linhasCsv.join('\r\n');
};

const downloadCsv = (conteudo, nomeArquivo) => {
  const bom = '﻿';
  const blob = new Blob([bom + conteudo], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const TabelaChequesProrrogados = ({ resultado }) => {
  const linhas = useMemo(() => (resultado && resultado.linhas) || [], [
    resultado,
  ]);
  const subtotaisPorAtivo = useMemo(
    () => (resultado && resultado.subtotais_por_ativo) || [],
    [resultado]
  );
  const totalGeral = useMemo(
    () =>
      (resultado && resultado.total_geral) || {
        total_valor: 0,
        quantidade: 0,
      },
    [resultado]
  );

  const subtotaisMap = useMemo(() => {
    const m = {};
    subtotaisPorAtivo.forEach((sub) => {
      m[sub.codigo_ativo] = sub;
    });
    return m;
  }, [subtotaisPorAtivo]);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    setPaginaAtual(1);
  }, [resultado]);

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(linhas.length / ITENS_POR_PAGINA)),
    [linhas.length]
  );

  const paginaSegura = Math.min(paginaAtual, totalPaginas);

  const linhasPagina = useMemo(() => {
    const inicio = (paginaSegura - 1) * ITENS_POR_PAGINA;
    return linhas.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [linhas, paginaSegura]);

  const linhasComSubtotal = useMemo(() => {
    const itens = [];
    let ativoCorrente = null;
    linhasPagina.forEach((linha) => {
      if (ativoCorrente != null && linha.codigo_ativo !== ativoCorrente) {
        const sub = subtotaisMap[ativoCorrente];
        if (sub) {
          itens.push({ tipo: 'subtotal', codigoAtivo: ativoCorrente, sub });
        }
      }
      itens.push({ tipo: 'dado', linha });
      ativoCorrente = linha.codigo_ativo;
    });
    if (ativoCorrente != null) {
      const sub = subtotaisMap[ativoCorrente];
      if (sub) {
        itens.push({ tipo: 'subtotal', codigoAtivo: ativoCorrente, sub });
      }
    }
    return itens;
  }, [linhasPagina, subtotaisMap]);

  const handleExportar = () => {
    if (!linhas.length) return;
    setExportando(true);
    try {
      const conteudo = construirConteudoCsv(linhas, subtotaisPorAtivo, totalGeral);
      const sufixo = moment().format('YYYYMMDD-HHmm');
      downloadCsv(conteudo, `cheques-prorrogados-${sufixo}.csv`);
    } finally {
      setExportando(false);
    }
  };

  const mudarPagina = (n) => {
    if (n < 1 || n > totalPaginas) return;
    setPaginaAtual(n);
  };

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const itens = [];
    const maxBotoes = 5;
    itens.push(
      <Pagination.First
        key="first"
        onClick={() => mudarPagina(1)}
        disabled={paginaSegura === 1}
      />
    );
    itens.push(
      <Pagination.Prev
        key="prev"
        onClick={() => mudarPagina(paginaSegura - 1)}
        disabled={paginaSegura === 1}
      />
    );

    let inicio = Math.max(1, paginaSegura - Math.floor(maxBotoes / 2));
    let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
    if (fim - inicio < maxBotoes - 1) {
      inicio = Math.max(1, fim - maxBotoes + 1);
    }
    if (inicio > 1) itens.push(<Pagination.Ellipsis key="el-i" disabled />);
    for (let n = inicio; n <= fim; n += 1) {
      itens.push(
        <Pagination.Item
          key={`pag-${n}`}
          active={n === paginaSegura}
          onClick={() => mudarPagina(n)}
        >
          {n}
        </Pagination.Item>
      );
    }
    if (fim < totalPaginas)
      itens.push(<Pagination.Ellipsis key="el-f" disabled />);
    itens.push(
      <Pagination.Next
        key="next"
        onClick={() => mudarPagina(paginaSegura + 1)}
        disabled={paginaSegura === totalPaginas}
      />
    );
    itens.push(
      <Pagination.Last
        key="last"
        onClick={() => mudarPagina(totalPaginas)}
        disabled={paginaSegura === totalPaginas}
      />
    );

    const inicioRegistro =
      linhas.length > 0 ? (paginaSegura - 1) * ITENS_POR_PAGINA + 1 : 0;
    const fimRegistro = Math.min(
      paginaSegura * ITENS_POR_PAGINA,
      linhas.length
    );

    return (
      <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
        <div className="text-muted small">
          Mostrando {inicioRegistro} até {fimRegistro} de {linhas.length}{' '}
          registros
        </div>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  if (linhas.length === 0) {
    return (
      <Card className="shadow-sm">
        <Card.Body>
          <Alert variant="info" className="mb-0">
            {MENSAGEM_VAZIO}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center bg-light">
        <strong>Resultado ({linhas.length} cheques)</strong>
        <Button
          variant="success"
          size="sm"
          onClick={handleExportar}
          disabled={exportando || linhas.length === 0}
        >
          <FaFileExcel className="mr-1" />
          {exportando ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table striped bordered hover size="sm" className="mb-0">
            <thead className="thead-light">
              <tr>
                <th>Cód. Ativo</th>
                <th>GE</th>
                <th>Cód. Proposta</th>
                <th>Conta Cedente</th>
                <th>Apelido Cedente</th>
                <th>Conta Portador</th>
                <th>Apelido Portador</th>
                <th className="text-right">Valor</th>
                <th>Vencimento</th>
                <th>Efetivação</th>
                <th>CGC/CPF</th>
                <th>Nº Cheque</th>
                <th className="text-right">Prorrogações</th>
                <th className="text-right">Prorrogou</th>
              </tr>
            </thead>
            <tbody>
              {linhasComSubtotal.map((item) => {
                if (item.tipo === 'subtotal') {
                  return (
                    <tr
                      key={`sub-${item.codigoAtivo}`}
                      className="font-weight-bold"
                      style={{ backgroundColor: '#eef3ff' }}
                    >
                      <td colSpan={7} className="text-right">
                        Subtotal Ativo {item.codigoAtivo} (
                        {item.sub.quantidade} cheques):
                      </td>
                      <td className="text-right">
                        {formatarMonetario(item.sub.total_valor)}
                      </td>
                      <td colSpan={6} />
                    </tr>
                  );
                }
                const l = item.linha;
                return (
                  <tr key={`${l.codigo_ativo}-${l.codigo_titulo}`}>
                    <td>{l.codigo_ativo}</td>
                    <td>{l.codigo_grupo_empresa}</td>
                    <td>{l.codigo_proposta}</td>
                    <td>{l.conta_cedente}</td>
                    <td>{l.apelido_cedente}</td>
                    <td>{l.conta_portador}</td>
                    <td>{l.apelido_portador}</td>
                    <td className="text-right">
                      {formatarMonetario(l.valor)}
                    </td>
                    <td>{formatarData(l.data_vencimento)}</td>
                    <td>{formatarData(l.data_efetivacao)}</td>
                    <td>{formatarCgcCpf(l.cgc_cpf)}</td>
                    <td>{l.numero_cheque_rec}</td>
                    <td className="text-right">{l.prorrogacoes}</td>
                    <td className="text-right">{l.prorrogou}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr
                className="font-weight-bold"
                style={{ backgroundColor: '#dde7ff' }}
              >
                <td colSpan={7} className="text-right">
                  Total geral ({totalGeral.quantidade} cheques):
                </td>
                <td className="text-right">
                  {formatarMonetario(totalGeral.total_valor)}
                </td>
                <td colSpan={6} />
              </tr>
            </tfoot>
          </Table>
        </div>
      </Card.Body>
      <Card.Footer className="bg-white">
        <Row>
          <Col>{renderizarPaginacao()}</Col>
        </Row>
      </Card.Footer>
    </Card>
  );
};

export default TabelaChequesProrrogados;
